/** Time helpers. All customer-facing times display in America/New_York (ET).
 *  All datetimes exchanged with the API are UTC ISO 8601.
 */

export const TZ_ET = "America/New_York";

/** Today's date in ET as YYYY-MM-DD. */
export function todayInET() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ_ET,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Convert ET wall-clock (YYYY-MM-DD, HH:MM) to a UTC ISO string. */
export function etDateTimeToUtcIso(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const wantedWallUtcMs = Date.UTC(y, m - 1, d, hh, mm, 0);
  // Ask what the current guess (interpreted as UTC) looks like when
  // formatted in ET, then correct the offset.
  const guess = new Date(wantedWallUtcMs);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ_ET,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(guess);
  const g = (t) => Number(parts.find((p) => p.type === t)?.value);
  // "24" is a legit hour in en-CA when the moment sits at exact midnight.
  const etHour = g("hour") === 24 ? 0 : g("hour");
  const gotWallUtcMs = Date.UTC(g("year"), g("month") - 1, g("day"), etHour, g("minute"), g("second"));
  const offsetMs = wantedWallUtcMs - gotWallUtcMs;
  return new Date(wantedWallUtcMs + offsetMs).toISOString();
}

/** Format a UTC ISO string as an ET wall-clock time HH:MM. */
export function isoToEtTime(iso) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ_ET,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** Human-friendly ET date, e.g. "Friday, Mar 20, 2026". */
export function isoToEtLongDate(iso) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ_ET,
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function dateToLongLabel(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(y, m - 1, d));
}

/** From an availability window {start_time, end_time} (last valid start),
 *  produce start-time slot options at 30-min increments in HH:MM. */
export function windowsToSlots(windows) {
  const slots = [];
  const seen = new Set();
  for (const w of windows || []) {
    const [sh, sm] = w.start_time.split(":").map(Number);
    const [eh, em] = w.end_time.split(":").map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    // Round up to next :00 or :30.
    let m = Math.ceil(startMin / 30) * 30;
    if (m < startMin) m += 30;
    while (m <= endMin) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      const label = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
      if (!seen.has(label)) {
        seen.add(label);
        slots.push(label);
      }
      m += 30;
    }
  }
  return slots;
}

/** Pretty label for "17:30" -> "5:30 PM". */
export function prettyTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}
